import { Deck } from "@/lib/classes/deck";
import { UnitCategory } from "@/lib/types/deck-builder";
import { CompactPackCard } from "./CompactPackCard";

interface DeckPreviewProps {
  deck: Deck;
}

// Helper function to get category code from category enum
function getCategoryCode(category: UnitCategory): string {
  const codeMap: Record<UnitCategory, string> = {
    [UnitCategory.LOG]: "LOG",
    [UnitCategory.INF]: "INF",
    [UnitCategory.ART]: "ART",
    [UnitCategory.TNK]: "TNK",
    [UnitCategory.REC]: "REC",
    [UnitCategory.AA]: "AA",
    [UnitCategory.HEL]: "HEL",
    [UnitCategory.AIR]: "AIR",
    [UnitCategory.DEFENSE]: "DEF",
  };
  return codeMap[category] || category.split("/").pop() || "";
}

export function DeckPreview({ deck }: DeckPreviewProps) {
  const groupedUnits = deck.unitsInDeckGroupedUnitsByCategory;
  const categories = deck.unitCategories.filter(
    (category) => (groupedUnits[category] || []).length > 0
  );

  return (
    <div className="relative w-full">
      {/* Preview Content */}
      <div className="flex flex-col gap-1 w-full px-0 py-2">
        {/* Category Sections */}
        {categories.map((category) => {
          const units = groupedUnits[category] || [];
          const usedSlots = units.length;
          const totalSlots = deck.getTotalSlotsForCategory(category);
          const unitCount = deck.getTotalUnitCountForCategory(category);
          const categoryPoints = deck.getSumOfUnitCostsForCategory(category);
          const slotCosts = deck.slotCosts[category] || [];
          const categoryAP = slotCosts.slice(0, usedSlots).reduce((sum, cost) => sum + cost, 0);

          return (
            <div
              key={category}
              className="bg-muted/30 rounded border border-border overflow-hidden flex"
            >
              {/* Category Info - Left Side */}
              <div className="px-1.5 py-0.5 bg-muted/50 border-r border-border flex-shrink-0 min-w-[110px]">
                <div className="flex flex-col gap-0">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground leading-tight">
                    {getCategoryCode(category)}
                  </h3>
                  <div className="flex flex-col gap-0 text-[10px] text-muted-foreground leading-tight">
                    <span className="font-medium">
                      {usedSlots} / {totalSlots} slots
                    </span>
                    <span>{unitCount} units</span>
                    <span>{categoryPoints} points</span>
                    <span className="font-medium">{categoryAP} AP</span>
                  </div>
                </div>
              </div>

              {/* Pack Cards Grid - Right Side */}
              <div className="flex-1 p-1 grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-1.5">
                {units.map((deckUnit, index) => (
                  <CompactPackCard
                    key={`${deckUnit.pack.packDescriptor}-${index}`}
                    deck={deck}
                    deckUnit={deckUnit}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

